import { Auth0Client } from '@auth0/auth0-spa-js';
import * as Sentry from '@sentry/browser';
import { isOfcAccess } from './ofcAccess';

let auth0client;

// Routes that leave the page right away never need the session restored in
// the background: /login starts a new authorization, /logout ends it and the
// Auth0 callback restores it by exchanging its own code.
const needsSessionCheck = () => {
  // An Argo access (PE-2215) works without Auth0 and must never contact it.
  if (isOfcAccess()) {
    return false;
  }

  const { pathname, search } = window.location;

  if (search.includes('code=') && search.includes('state=')) {
    return false;
  }

  return ![process.env.ROUTE_LOGIN, process.env.ROUTE_LOGOUT].includes(
    pathname
  );
};

export const getAuth0Client = (customOptions = {}) => {
  if (auth0client) {
    return auth0client;
  }

  const auth0Domain = process.env.AUTH0_DOMAIN;
  const auth0ClientId = process.env.AUTH0_CLIENT_ID;

  const defaultOptions = {
    domain: auth0Domain,
    client_id: auth0ClientId,
    redirect_uri: `${window.location.origin}/`,
    scope: 'openid profile email user_metadata picture',
    audience: `https://${auth0Domain}/api/v2/`,
    ui_locales: 'it',
    language: 'it',
    screen_hint: 'login',
    cacheLocation: 'localstorage',
  };

  const params = Object.assign({}, defaultOptions, customOptions);

  const client = new Auth0Client(params);

  // Like `createAuth0Client`, but a failed silent check does not reject the
  // client: some browsers never complete the Auth0 token request (PE-2238)
  // and the rejected promise froze /login, /logout and /auth until a reload.
  // Without a cached session the user is simply not authenticated and goes
  // through /login, as auth0-spa-js 2.x does.
  const sessionChecked = needsSessionCheck()
    ? client.checkSession().catch((error) => {
        Sentry.captureException(error);
      })
    : Promise.resolve();

  auth0client = sessionChecked.then(() => client);

  return auth0client;
};
